import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ErroApi, mensagemDoErro } from '../api';
import {
  descartarLeitura, descartarUmidade, gravarCabecalho, gravarEstagios,
  gravarFechamento, gravarUmidades, verAdensamento,
  verLeituras, importarBruto, enviarFotos, conferirTensao,
} from '../api/adensamento';
import type {
  ConferenciaBruto, ConferenciaTensao, TipoFoto,
} from '../api/adensamento';
import { numero, paraCampo, paraNumero, rotulo, sinal } from '../formato';
import { tecladoDaTabela } from '../tecladoDaTabela';
import { Pendencias } from '../Pendencias';
import type { PendenciaDetalhada } from '../Pendencias';
/* Reexportadas porque `AdensamentoInterpretacao` as importa daqui desde
   antes de existir arquivo próprio — mover o import lá seria mexer numa
   tela por causa de um arquivo. */
export { Pendencias };
export type { PendenciaDetalhada };
import type { Falta } from './AdensamentoGraficos';
export type { Falta };
import { useSessao } from '../sessao';
import {
  AncoraDePendencias, CabecalhoDaFicha, EscolhaDeBalanca, EtapasDaFicha,
  JanelaDeDescarte, RodapeDeEtapas, useEquipamentos, useFicha,
} from '../Ficha';
import type { EquipamentoDaLista, Etapa } from '../Ficha';
import { CapturaDeFoto } from '../CapturaDeFoto';
import { gabaritoDoMetodo } from '../gabaritos';
import {
  AcoesDoLaudo, Area, Aviso, Campo,
  Carregando, ErroDaApi, Escolha, Selo,
  EscolhaPorCodigo, comoCodigo, ZonaDeArquivo, RespostaDaAcao,
} from '../ui';
import type { Opcao } from '../ui';
import {
  LembreteDaEstufa, SecaDaLinha, WDaLinha, naEstufaDaFicha, useEstufaDoEnsaio,
} from './NaEstufa';
import { nomeDaAmostra } from '../NomeDaAmostra';

/**
 * Lançar um adensamento — a BANCADA.
 *
 * ─── Esta tela é de uma pessoa só ──────────────────────────────────────
 *
 * Até 23/08/2026 ela ia do anel à tensão de pré-adensamento, e Humberto
 * apontou o defeito com uma frase: «está misturado a tela do
 * laboratorista com a tela da pessoa que irá elaborar o relatório».
 *
 * Estava mesmo. O que se lança aqui são FATOS — o que a balança marcou, o
 * que a prensa registrou, quantos gramas sobraram no desmonte. O que
 * saiu daqui são INTERPRETAÇÕES — t₅₀, t₉₀, os pares de estágio de
 * Cr/Cc/Cs e a σ'p, que se traçam a régua sobre a curva impressa e têm o
 * nome de quem traçou. São ofícios diferentes, feitos em momentos
 * diferentes, por pessoas com permissões diferentes (`ensaio.lancar` ×
 * `ensaio.processar` — a API já os separava desde o primeiro dia).
 *
 * A interpretação mudou-se para `/ensaio/:id/adensamento/interpretacao`.
 * Daqui sai um link, e um resumo do que já foi traçado — porque o
 * laboratorista precisa VER que a etapa seguinte andou, sem poder
 * assiná-la.
 *
 * ─── A ordem da tela é a ordem da bancada ──────────────────────────────
 *
 *   1. corpo de prova e ensaio     — moldagem
 *   2. umidade inicial             — moldagem
 *   3. estágios de pressão         — abertura do ensaio
 *   4. fechamento                  — desmonte: massa final e umidade final
 *   5. fotos do corpo de prova     — moldagem e desmonte
 *   6. dados brutos da prensa      — o arquivo do equipamento
 *   7. leituras                    — o que veio do arquivo
 *   8. interpretação               — o resumo do que foi traçado
 *   9. laudo                       — a emissão
 *
 * Até a 183 o arquivo da prensa vinha no 4 e o desmonte no 7. A 184
 * pôs a bancada inteira antes da máquina («primeiro os inputs que são
 * feitos em laboratório — umidades, massas —, e só depois a importação
 * da planilha de dados brutos», Humberto, 11/09/2026): tudo o que a
 * mão mede e fotografa vem antes do que o equipamento gravou.
 *
 * ─── Os números são a ordem, e por um tempo eles mentiram ─────────────
 *
 * Até 01/09/2026 dois blocos diferentes se chamavam «5» — leituras e
 * fechamento — e não havia nenhum «7». Ninguém tinha reparado porque a
 * sequência se lê rolando, e rolando ninguém soma. Quem descobriu foi um
 * teste que procurava o bloco 5 pelo número e achou dois.
 *
 * O número não é enfeite: é como o laboratorista fala do bloco («parei no
 * 4») e como esta lista se confere. Dois cincos fazem essa frase deixar
 * de significar uma coisa só.
 *
 * A umidade final saiu do bloco 2, onde estava junto da inicial, e foi
 * para o 7. Ela não existe no dia da moldagem: aparece uma ou duas
 * semanas depois, quando o corpo de prova sai do anel. Um campo em
 * branco que fica em branco por semanas ensina a ignorar campo em
 * branco.
 *
 * ─── O que a tela NÃO faz ──────────────────────────────────────────────
 *
 * **Não calcula.** Todo número derivado — umidade, e₀, ρd, S — vem do
 * servidor, que o lê de view do banco (`D-29`).
 *
 * **Não pergunta o que o cadastro já respondeu.** Tipo da amostra vem da
 * amostra; sentido da leitura vem da prensa. Ver a `0055`.
 *
 * **Não apaga.** Leitura ou determinação erradas se DESCARTAM, com
 * motivo. A linha continua no banco, aparece riscada, e a média passa a
 * ignorá-la.
 *
 * ─── Por que os números entram como texto ──────────────────────────────
 *
 * `<input type="number">` no celular abre teclado numérico, mas em pt-BR
 * ele briga com a vírgula: o navegador chama "1,55" de inválido e devolve
 * string vazia, sem avisar. O campo é `inputMode="decimal"` e a conversão é
 * `paraNumero`, que aceita vírgula.
 */

// ── formulário: número que aceita vírgula ────────────────────────────

export interface Numerico {
  [campo: string]: string;
}

/** Lê um campo do formulário como número; `undefined` quando em branco. */
export const n = (f: Numerico, campo: string) => paraNumero(f[campo] ?? '');

// ── tipos que o servidor devolve ─────────────────────────────────────

export interface Estagio {
  id: string;
  ordem: number;
  fase: string;
  pressao_kpa: string;
  inundado: boolean;
  altura_cm: string | null;
  indice_vazios: string | null;
  /** e₀ — o índice de vazios ANTES do primeiro carregamento. É o mesmo
      para todos os estágios; vem da view, que o calcula uma vez. Entra na
      construção de Pacheco Silva. */
  indice_vazios_inicial: string | null;
  altura_solidos_cm: string | null;
  leituras: number;
  cv_metodo: string | null;
  /** De onde vieram os números do cv (0155): tela, digitado, nao_registrada. */
  cv_origem?: string | null;
  /** 0183/187: os dois métodos gravados no estágio, quando há. */
  cvs?: {
    metodo: string; cv_cm2_s: string | number | null; no_laudo: boolean;
    origem: string | null;
    h0_mm: string | null; h90_mm: string | null; h100_mm: string | null;
    t90_s: string | null; t50_s: string | null; t1_s: string | null; t2_s: string | null;
  }[];
  /** O cv que a view calcula a partir do traçado gravado (`0025`). */
  cv_cm2_s: string | null;
  t50_s: string | null;
  t90_s: string | null;
  h0_mm: string | null;
  h90_mm: string | null;
  h100_mm: string | null;
  cv_responsavel_id: string | null;
}

export interface Umidade {
  id: string;
  momento: string;
  ordem: number;
  capsula: string | null;
  capsula_equipamento_id: string | null;
  tara: string | null;
  massa_capsula_solo_umido: string | null;
  massa_capsula_solo_seco: string | null;
  umidade: string | null;
  descartada: boolean;
  motivo_descarte: string | null;
}

export interface Leitura {
  id: string;
  tempo_s: string;
  leitura_mm: string;
  origem: string;
  descartada: boolean;
  motivo_descarte: string | null;
}

export interface Capsula {
  id: string;
  codigo_patrimonial: string;
  nome: string;
  /** A tara que vale para a DATA deste ensaio, vinda da aferição. */
  tara: string | null;
  tara_unidade: string | null;
}

export interface Prensa {
  codigo: string;
  nome: string;
  fabricante: string;
  leitura_sentido: number;
  coluna_deslocamento: string | null;
  evidencia: string;
}

export interface Ensaio {
  /** A situação do ensaio — aprovado é pedra (UX-049). */
  status?: string;
  /** As balanças das duas baterias (RN-215, onda 239). */
  balanca?: { id: string; codigo: string; nome: string } | null;
  balancaUmidade?: { id: string; codigo: string; nome: string } | null;
  /** A amostra pelo cadastro (UX-028) — existe antes do cabeçalho. */
  amostra?: {
    codigo: string; tipo_amostra: string | null;
    /** Onda 303 (RN-248): o nome que o cliente deu à amostra. */
    identificacao_recebida?: string | null;
    tipo_acondicionamento: string | null; acondicionamento_descricao: string | null;
  } | null;
  cabecalho: any;
  inicial: any;
  final: any;
  estagios: Estagio[];
  umidades: Umidade[];
  indices: any[];
  pre: any;
  equipamentos: any[];
  prensas: Prensa[];
  capsulas: Capsula[];
  pendencias: string[];
  /* A lista com código e justificativa (0089). `pendencias` continua
     sendo só as ABERTAS, em texto, porque é assim que as outras seis
     telas ainda a consomem. */
  pendenciasDetalhadas: PendenciaDetalhada[];
  /* O que impede a curva de compressibilidade, com o certificado onde a
     constante já está medida (`0103`). */
  faltas?: Falta[];
}

export interface Equipamento {
  id: string;
  codigo_patrimonial: string;
  nome: string;
  categoria: string;
}

export interface Funcionario { id: string; nome: string }

export const num = (v: unknown, casas: number) => numero(v, casas);

/**
 * Categorias que servem de anel de adensamento.
 *
 * `anel` é a genérica aposentada pela `0039`, e continua na lista porque
 * o parque tem equipamento antigo cadastrado nela — esconder o anel de
 * quem já o tem seria consertar o catálogo criando um impedimento.
 */
const CAT_ANEL = ['anel_adensamento', 'anel'];

/**
 * Categorias que servem de prensa de adensamento.
 *
 * «No relatório PDF deverá constar qual o equipamento foi utilizado (no
 *  caso, qual prensa de adensamento foi utilizada)» — Testesolo,
 * 01/09/2026.
 *
 * A multiprensa entra na lista porque é uma prensa de adensamento com
 * várias células: quem roda um adensamento na MP50 rodou numa prensa de
 * adensamento, e o parque a cadastra na categoria própria dela.
 */
const CAT_PRENSA = ['prensa_adensamento', 'multiprensa'];

/**
 * Quantas linhas a planilha de umidade mostra sempre.
 *
 * Três, que é o que a 6.2 pede. Não é um teto: determinação descartada
 * continua ocupando o seu número, e a substituta entra como quarta. O que
 * a constante define é o piso — a planilha nasce com as três linhas
 * abertas, do jeito que ela está impressa na ficha da bancada.
 */
const LINHAS_UMIDADE = 3;

/**
 * Tudo que as duas telas do adensamento precisam carregar.
 *
 * Um gancho, e não uma cópia em cada arquivo: a tela da bancada e a da
 * interpretação leem o MESMO ensaio, e duas rotinas de carregamento
 * seriam duas oportunidades de divergir sobre o que é "recarregar".
 */
export function useAdensamento(id: string) {
  const [d, setD] = useState<Ensaio | null>(null);
  const [equip, setEquip] = useState<Equipamento[]>([]);
  const [gente, setGente] = useState<Funcionario[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setD(await verAdensamento(id));
    } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  useEffect(() => {
    void (async () => {
      try {
        const [e, f] = await Promise.all([
          api<Equipamento[]>('/equipamentos?limite=500'),
          api<Funcionario[]>('/funcionarios'),
        ]);
        setEquip(Array.isArray(e) ? e : []);
        setGente(Array.isArray(f) ? f : []);
      } catch { /* os seletores caem para texto livre */ }
    })();
  }, []);

  /**
   * Toda gravação passa por aqui.
   *
   * Um lugar só para: marcar o que está salvando, guardar o erro no
   * formato da API (que a `ErroDaApi` sabe abrir campo a campo), e
   * recarregar o ensaio inteiro com a resposta — que já vem completa,
   * justamente para a tela não ter que remontar o estado à mão e
   * arriscar divergir do banco.
   *
   * Ele recebe a CHAMADA pronta, e não um caminho para montar. Os
   * caminhos moram em `api/adensamento.ts`, escritos por extenso — quem
   * procurar `/adensamento/umidades` no projeto acha a chamada, e o teste
   * `26`, que confere se toda rota de criação da API tem caminho na tela,
   * consegue enxergá-las.
   */
  const gravar = useCallback(async (
    marca: string, chamada: () => Promise<Ensaio>,
  ) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      setD(await chamada());
      setOk('Gravado.');
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, []);

  const opcoesEquip = useCallback((cats: string[]): Opcao[] => equip
    .filter((e) => cats.includes(e.categoria))
    .map((e) => ({ valor: e.id, texto: e.codigo_patrimonial })), [equip]);

  const opcoesGente: Opcao[] = gente.map((g) => ({ valor: g.id, texto: g.nome }));

  const campoErro = (c: string) =>
    erro instanceof ErroApi ? erro.campo(c) : undefined;

  return { d, erro, ok, salvando, gravar, carregar, opcoesEquip, opcoesGente,
           campoErro };
}

export function Adensamento() {
  const { id = '' } = useParams();
  const { pode } = useSessao();
  const { d, erro, ok, salvando, gravar, carregar, opcoesEquip, campoErro } =
    useAdensamento(id);
  const equipamentos = useEquipamentos();
  const ficha = useFicha(id, 'NBR 16853', ok);
  /*
   * A sequência que o bloco 6 sugere ao bloco 3 (04/09/2026: «ao anexar
   * um arquivo de dados brutos, não estou conseguindo fazer o software
   * utilizar os valores»). O arquivo não traz a tensão nominal — traz a
   * que o servo motor conseguiu aplicar —, então a sugestão é a média
   * lida, arredondada, e quem lança ajusta para a nominal antes de gravar.
   */
  const [sugestaoSequencia, setSugestaoSequencia] = useState<string | null>(null);

  /*
   * A tela em ETAPAS (Humberto, 14/09/2026): «o lançamento do ensaio de
   * adensamento está ficando muito comprido. Sugiro separarmos em etapas,
   * igual ao recebimento de uma amostra, pois o processo é feito em
   * etapas. É feita a moldagem (definido o anel e massa inicial, umidade
   * inicial), definida a prensa e definidas as tensões, inundado ou não,
   * tipo de anel. Depois é feita a massa final, umidade final. Depois é
   * feita a interpretação.»
   *
   * Os blocos continuam os mesmos, com os mesmos números — só deixam de
   * aparecer todos de uma vez. A etapa vem da URL (`?etapa=`), para o
   * link de uma pendência abrir na etapa certa e o F5 não perder o lugar;
   * sem nada na URL, abre a primeira etapa que ainda tem trabalho.
   */
  const [params, setParams] = useSearchParams();

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o ensaio" />;

  const congelado = ['aprovado', 'cancelado', 'substituido'].includes(d.status ?? '');

  const i = d.inicial ?? {};
  const temMoldagem = !!d.cabecalho?.anel_equipamento_id && i.umidade_inicial != null
    && d.estagios.length > 0;
  const temLeituras = d.estagios.some((e) => e.leituras > 0);
  const temDesmonte = i.massa_final_g != null && i.umidade_final != null;
  const etapaSugerida = !temMoldagem ? 1 : !temLeituras ? 2 : !temDesmonte ? 3 : 4;
  const etapaPedida = Number(params.get('etapa'));
  const etapa = etapaPedida >= 1 && etapaPedida <= 4 ? etapaPedida : etapaSugerida;
  const irPara = (n: number) => {
    const q = new URLSearchParams(params);
    q.set('etapa', String(n));
    setParams(q, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const ETAPAS: [string, boolean][] = [
    ['Moldagem', temMoldagem],
    ['Leituras', temLeituras],
    ['Desmonte', temDesmonte],
    /* RN-238: a interpretação só aparece para quem tem `ensaio.interpretar`. */
    [pode('ensaio.interpretar') ? 'Interpretação e laudo' : 'Laudo', d.status === 'aprovado'],
  ];
  const RodapeAntigo = () => (
    <div className="acoes etapas-rodape">
      {etapa > 1 && (
        <button type="button" className="botao secundario" onClick={() => irPara(etapa - 1)}>
          ← {ETAPAS[etapa - 2][0]}
        </button>
      )}
      {etapa < 4 && (
        <button type="button" className="botao" onClick={() => irPara(etapa + 1)}>
          {ETAPAS[etapa][0]} →
        </button>
      )}
    </div>
  );

  /*
   * No Carbon (onda 239): o cabeçalho e as etapas de `Ficha.tsx`. A faixa
   * continua sendo a do adensamento (`ResumoDoEnsaio`, 102) — ela já era
   * a faixa de resultado com o «i» de cada fórmula, e é dela que o
   * desenho do Carbon saiu.
   */
  const etapasFicha: Etapa[] = ETAPAS.map(([nome, feita], k) => ({
    nome, feita,
    falta: k === 0 && (d.pendenciasDetalhadas ?? []).some(
      (p) => p.trava && !p.justificada && p.codigo.startsWith('balanca_')),
  }));
  void RodapeAntigo;

  return (
    <div className="tela adensamento ficha-carbon">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? 'ADNS'}
        titulo="Adensamento unidimensional" ficha={ficha}
        normaDaTela="ABNT NBR 16853" />

      <ResumoDoEnsaio d={d} />

      <EtapasDaFicha etapas={etapasFicha} atual={etapa} irPara={irPara} />

      <AncoraDePendencias>
        <Pendencias itens={d.pendencias} id={id}
          detalhe={d.pendenciasDetalhadas} aoJustificar={carregar} />
      </AncoraDePendencias>

      <RespostaDaAcao erro={erro} ok={ok ?? null} />

      {/*
        * Aprovado é pedra — e a tela diz isso ANTES de alguém digitar
        * (UX-049). O banco já recusava (`fn_bloqueia_dado_de_ensaio_
        * aprovado`, 0061); o que faltava era a bancada não parecer
        * editável. O `<fieldset disabled>` desliga tudo de uma vez —
        * campos, botões, arquivo, foto — sem que cada bloco precise
        * saber da regra.
        */}
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(d.status)} — só leitura`}>
          {d.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}

      <fieldset className="so-leitura" disabled={congelado}>
      {etapa === 1 && (
        <>
          <Cabecalho
            d={d} id={id} gravar={gravar} salvando={salvando} campoErro={campoErro}
            equipamentos={equipamentos}
            opcoesAnel={opcoesEquip(CAT_ANEL)}
            opcoesPrensa={opcoesEquip(CAT_PRENSA)}
          />

          <Umidades
            equipamentos={equipamentos}
            titulo="2 · Umidade inicial" momento="inicial"
            explicacao="Três determinações na moldagem (6.2). A tara vem da
              calibração interna da cápsula; a média ignora as descartadas."
            d={d} id={id} gravar={gravar} salvando={salvando}
            recarregar={carregar}
          />

          <Estagios d={d} id={id} gravar={gravar} salvando={salvando}
            sugestao={sugestaoSequencia} />
        </>
      )}

      {/*
        * A ordem é a da bancada, e a bancada vem ANTES do arquivo (184):
        * «o layout do ensaio de adensamento deve conter primeiro os inputs
        * que são feitos em laboratório (umidades, massas), e só depois a
        * importação da planilha de dados brutos» — Humberto, 11/09/2026.
        * O desmonte (massa final, umidade final) e as fotos são medidas
        * e registros da pessoa; o arquivo da prensa é o que a máquina
        * gravou, e entra depois de tudo o que a mão lançou.
        */}
      {etapa === 3 && (
        <>
          <Fechamento
            d={d} id={id} gravar={gravar} salvando={salvando} campoErro={campoErro}
            equipamentos={equipamentos}
            recarregar={carregar}
          />

          <FotosCp d={d} id={id} recarregar={carregar} />
        </>
      )}

      {etapa === 2 && (
        <>
          <Brutos d={d} id={id} recarregar={carregar}
            aoSugerirSequencia={(seq) => {
              setSugestaoSequencia(seq);
              irPara(1);
              setTimeout(() => document.getElementById('bloco-estagios')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
            }} />

          <Leituras d={d} id={id} gravar={gravar} salvando={salvando} />
        </>
      )}
      </fieldset>

      {etapa === 4 && (
        <>
          {pode('ensaio.interpretar') && (
            <Interpretacao d={d} id={id} podeProcessar />
          )}

          <Laudo id={id} pendencias={d.pendencias} />
        </>
      )}

      <RodapeDeEtapas etapas={etapasFicha} atual={etapa} irPara={irPara}
        nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
    </div>
  );
}

// ── o estado de um bloco, no próprio título ──────────────────────────

/**
 * O que este bloco tem, dito no título dele.
 *
 * «11 estágios · último 800 kPa» responde «o que falta aqui?» sem que
 * ninguém precise abrir o bloco e contar linhas. É o item 1 do documento
 * 75, tirado das telas da Suporte Infra, onde todo bloco recolhido traz
 * um cabeçalho vivo: `média w₀ = 25,31%`, `0 leitura(s)`, `SEM DADOS`.
 *
 * Aqui ele fica no `<h2>` e não num acordeão, porque os nossos blocos não
 * se recolhem: a tela é uma sequência de trabalho, e recolher o bloco 3
 * enquanto se digita o 5 esconderia o estágio contra o qual se confere a
 * leitura. O que a pessoa precisa, rolando, é saber onde já passou.
 *
 * ─── Vazio é um estado, e ele se diz ───────────────────────────────
 *
 * Bloco sem nada não fica sem etiqueta: fica com «nenhuma», «sem
 * arquivo», «sem foto». É o `CA-29` — a diferença entre «não olhei» e
 * «olhei e não tem» é a que faz alguém voltar aqui ou seguir adiante.
 */
function Estado({ children }: { children: React.ReactNode }) {
  return <span className="estado-do-bloco">{children}</span>;
}


// ── a faixa de resultado ─────────────────────────────────────────────

/**
 * O resultado do ensaio, à vista o tempo todo.
 *
 * A tela tem nove blocos e rola por três telas de altura. Quem está
 * digitando a leitura do estágio 7 não vê mais o e₀ nem a σ'p — e são
 * justamente os números contra os quais se confere se o que se está
 * digitando faz sentido. Um recalque que derruba o índice de vazios
 * abaixo de zero é erro de digitação, e quem não tem o e₀ na tela
 * descobre isso três dias depois.
 *
 * A faixa é GRUDADA no topo (`position: sticky`) e mostra o que a
 * bancada precisa conferir de relance: e₀, w inicial, ρd, σ′p e os três
 * coeficientes. Foi o que a análise das telas da Suporte Infra apontou
 * como a coisa mais barata que faltava aqui (documento 75, item 2).
 *
 * ─── Ela não calcula nada ──────────────────────────────────────────
 *
 * Todos os números vêm do servidor, que os lê das views (`D-29`). A
 * faixa escolhe QUAIS mostrar e em que ordem; se ela fizesse a conta,
 * seria a segunda resposta possível para o mesmo número.
 *
 * ─── Célula vazia diz por que está vazia ───────────────────────────
 *
 * «—» sem explicação deixa o técnico entre «não foi medido» e «não dá
 * para calcular», que pedem ações opostas (CA-29). Cada célula sem
 * valor ganha um `title` que nomeia o que falta.
 */
function ResumoDoEnsaio({ d }: { d: Ensaio }) {
  const i = d.inicial;
  const indice = (trecho: string) =>
    (d.indices ?? []).find((x: any) => x.trecho === trecho);
  const cc = indice('compressao');
  const cr = indice('recompressao');
  const cs = indice('descompressao');

  const comLeitura = d.estagios.filter((e) => e.leituras > 0).length;

  /**
   * Uma célula: rótulo, valor e — quando vazia — o motivo.
   *
   * O motivo não é adivinhado aqui: quem chama passa a frase, porque só
   * quem sabe o que aquele número precisa sabe o que falta para ele.
   */
  /*
   * A fórmula de cada índice vai no `title` — aparece ao passar o mouse
   * (Humberto, 14/09/2026: «no cabeçalho não precisam ficar todas as
   * fórmulas de cada índice físico. Pode aparecer apenas quando passar o
   * mouse por cima de cada índice»). O texto é o mesmo que o antigo
   * `ComoSeObtem` mostrava aberto — transcrito das funções do banco que
   * fazem a conta, com a cláusula da norma.
   */
  const cel = (
    rotuloTexto: string, valor: unknown, casas: number,
    unidade: string, falta: string, formula?: string,
  ) => {
    const tem = valor !== null && valor !== undefined && valor !== '';
    return (
      <div key={rotuloTexto} className={tem ? '' : 'vazia'} title={formula}>
        <dt>{rotuloTexto}{formula ? <span className="dica-formula" aria-hidden="true"> ⓘ</span> : null}</dt>
        <dd title={tem ? formula : `${falta}${formula ? `\n\n${formula}` : ''}`}>
          {tem ? `${num(valor, casas)}${unidade}` : '—'}
        </dd>
      </div>
    );
  };

  return (
    <div className="faixa-resultado">
      <dl className="resumo-linha">
        {cel('e₀', d.estagios[0]?.indice_vazios_inicial, 4, '',
          'falta ρs, a massa do anel ou a umidade inicial',
          'e₀ = ρs / ρd − 1 (indice_vazios_inicial, 0024/0070). Não sai quando ρs não é maior que ρd. Hs = Hi / (1 + e₀) — 7.2.1.')}
        {cel('w inicial', i?.umidade_inicial, 2, ' %',
          'nenhuma determinação de umidade inicial gravada',
          'w = (Mu − Ms) / (Ms − Mt) × 100 (teor_umidade, 0022). A tara Mt é a da calibração interna vigente da cápsula. Média das determinações não descartadas.')}
        {cel('ρd', i?.massa_especifica_seca, 4, ' g/cm³',
          'falta a massa do anel com solo, a tara do anel ou o volume',
          'ρ = (massa do anel com solo − tara do anel) / volume do anel; ρd = ρ / (1 + w/100) (vw_adensamento_inicial, 0055). As constantes do anel vêm do certificado interno vigente.')}
        {cel('σ′p', d.pre?.pressao_kpa, 1, ' kPa',
          'a construção precisa do trecho virgem, traçado na interpretação',
          'σ′p pela construção de Pacheco Silva (ou Casagrande) sobre a curva e × log σ′ — 7.6. Traçada na interpretação, assinada por quem traçou.')}
        {cel('Cc', cc?.coeficiente, 3, '',
          'o trecho de compressão ainda não foi traçado',
          'Cc = |Δe / Δlog σ′| entre os dois estágios do trecho de compressão — 7.5.2 (vw_adensamento_indice, 0042).')}
        {cel('Cr', cr?.coeficiente, 3, '',
          'o trecho de recompressão ainda não foi traçado',
          'Cr = |Δe / Δlog σ′| entre os dois estágios do trecho de recompressão — 7.5.2.')}
        {cel('Cs', cs?.coeficiente, 3, '',
          'o trecho de descompressão ainda não foi traçado',
          'Cs = |Δe / Δlog σ′| entre os dois estágios do trecho de descompressão — 7.5.2. cv = 0,197 (0,5·H₅₀)²/t₅₀ (Casagrande) ou 0,848 (0,5·H₅₀)²/t₉₀ (Taylor) — 7.4.')}
        <div>
          <dt>Estágios</dt>
          <dd>{comLeitura} de {d.estagios.length} com leitura</dd>
        </div>
      </dl>
      <p className="sub dica-formulas">
        Passe o mouse sobre um índice para ver como ele foi calculado. Cada
        conta está no banco, em uma função só — a mesma que o laudo imprime.
      </p>
    </div>
  );
}

// ── 4 · o bruto da prensa ────────────────────────────────────────────

/**
 * Importar o arquivo da prensa — o PRIMEIRO passo dos dados.
 *
 * «Antes de iniciar a análise gráfica é preciso importar os dados brutos.
 *  Portanto o botão de importação deve vir antes e ser funcional.»
 *
 * A tela faz duas viagens de propósito. A primeira CONFERE: lê o arquivo,
 * casa cada etapa com o estágio de mesmo número e mostra, lado a lado, a
 * tensão nominal programada e a faixa que o servo motor realmente aplicou.
 * Só depois de a pessoa ver essa tabela é que aparece o botão que grava.
 *
 * Isso não é excesso de zelo. O vínculo é pelo NÚMERO do estágio — a carga
 * lida varia dentro do mesmo estágio, e no arquivo real o estágio 4 passeia
 * de 10,85 a 18,62 kPa. A conferência é onde alguém percebe que o arquivo
 * é do corpo de prova vizinho, e é a última hora em que dá para perceber.
 */
function Brutos({ d, id, recarregar, aoSugerirSequencia }: {
  d: any; id: string; recarregar: () => void;
  aoSugerirSequencia?: (sequencia: string) => void;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [prensa, setPrensa] = useState('ms115');
  const [conf, setConf] = useState<ConferenciaBruto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [feito, setFeito] = useState<string | null>(null);

  const jaImportado = (d.brutos ?? []) as any[];

  const rodar = async (simular: boolean) => {
    if (!arquivo) return;
    setOcupado(true); setErro(null); if (simular) setFeito(null);
    try {
      const r = await importarBruto(id, arquivo, prensa, simular);
      setConf(r);
      if (!simular) {
        setFeito(`${numero(r.leiturasImportadas ?? 0, 0)} leituras importadas.`);
        setArquivo(null);
        recarregar();
      }
    } catch (e) {
      setErro(mensagemDoErro(e));
      if (!simular) setConf(null);
    } finally { setOcupado(false); }
  };

  return (
    <section className="bloco">
      <h2>
        6 · Dados brutos da prensa
        <Estado>
          {jaImportado.length === 0 ? 'sem arquivo importado'
            : `${jaImportado.length} arquivo${jaImportado.length === 1 ? '' : 's'} importado`
              + `${jaImportado.length === 1 ? '' : 's'}`}
        </Estado>
      </h2>
      <p className="sub">
        O arquivo da Owntec, como ele saiu do equipamento — o <code>.csv</code>
        {' '}da MS 115 ou o <code>.xlsx</code> da MS 102 Pro. Ele fica guardado
        sem alteração; as leituras entram vinculadas ao <b>número</b> do
        estágio — nunca à carga lida, que o servo motor varia enquanto a
        amostra deforma.
      </p>

      {jaImportado.length > 0 && (
        <table className="dados">
          <thead>
            <tr><th>Arquivo</th><th>Prensa</th><th>Leituras</th>
              <th>Importado</th><th>Por</th></tr>
          </thead>
          <tbody>
            {jaImportado.map((b) => (
              <tr key={b.id}>
                <td className="mono">{b.nome_original}</td>
                <td>{rotulo(b.prensa)}</td>
                <td className="num">{numero(b.leituras_importadas, 0)}</td>
                <td>{new Date(b.importado_em).toLocaleString('pt-BR')}</td>
                <td>{b.importado_por_nome ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConferenciaDeTensao d={d} id={id} recarregar={recarregar} />

      <div className="linha-campos">
        {/*
          * As duas prensas, e o `octet-stream` porque é o que o tablet manda
          * quando não conhece a extensão. Filtro de `accept` que esconde o
          * arquivo certo é pior do que filtro nenhum: o leitor confere o
          * conteúdo de qualquer jeito.
          */}
        <ZonaDeArquivo
          rotulo="Arquivo do equipamento"
          aceita=".csv,.xlsx,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream"
          tipos="CSV ou XLSX"
          arquivos={arquivo}
          textoVazio="Arraste aqui o arquivo que saiu da prensa"
          dica="O .csv da MS 115 ou o .xlsx da MS 102 Pro, como o equipamento
            gravou. Ele fica guardado sem alteração."
          aoEscolher={(fs) => {
            setArquivo(fs[0] ?? null);
            setConf(null); setErro(null); setFeito(null);
          }}
        />
        <Escolha
          rotulo="Prensa" valor={prensa} aoEscolher={(v) => setPrensa(v ?? "ms115")}
          opcoes={[
            { valor: 'ms115', texto: 'Owntec MS 115' },
            { valor: 'ms102_pro', texto: 'Owntec MS 102 Pro' },
            { valor: 'outra', texto: 'Outra' },
          ]}
        />
      </div>

      <div className="acoes">
        <button
          type="button" className="principal" disabled={!arquivo || ocupado}
          onClick={() => rodar(true)}
        >
          {ocupado ? 'Lendo…' : 'Conferir arquivo'}
        </button>
        {conf?.simulacao && !conf.semEstagio.length && (
          <button
            type="button" disabled={ocupado} onClick={() => rodar(false)}
          >
            Importar {numero(conf.leiturasNoArquivo, 0)} leituras
          </button>
        )}
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {feito && <Aviso tipo="ok">{feito}</Aviso>}

      {conf && (
        <>
          <p className="sub">
            <b>{conf.arquivo}</b> · ensaio <span className="mono">
              {conf.idEnsaioNoArquivo ?? '—'}</span> · teste {conf.numeroTeste
              ?? '—'} · H₀ {numero(conf.alturaInicialMm, 2)} mm · Ø{' '}
            {numero(conf.diametroMm, 2)} mm ·{' '}
            {numero(conf.leiturasNoArquivo, 0)} leituras
          </p>

          {conf.avisos.map((a) => (
            <Aviso tipo="atencao" key={a}>{a}</Aviso>
          ))}

          {/*
            * Sem estágio programado, o arquivo não tem onde entrar — e a
            * tabela abaixo inteira em «não programado» não dizia o que
            * fazer. Aqui diz, e oferece o atalho: levar a sequência lida
            * para o bloco 3, onde ela vira nominal pela mão de quem lança.
            */}
          {conf.simulacao && conf.semEstagio.length > 0 && (
            <Aviso tipo="atencao" titulo={(d.estagios ?? []).length === 0
              ? 'Nenhum estágio programado ainda'
              : `${conf.semEstagio.length} estágio(s) do arquivo sem programação`}>
              As leituras entram vinculadas ao <b>número</b> do estágio, e a
              tensão nominal de cada um (a que vai para o laudo) não está no
              arquivo — a prensa grava só a que o servo motor conseguiu
              aplicar. Programe os estágios no bloco 3 e confira o arquivo de
              novo.
              {aoSugerirSequencia && (d.estagios ?? []).length === 0 && (
                <div className="acoes" style={{ marginTop: '.6rem' }}>
                  <button
                    type="button" className="principal"
                    onClick={() => aoSugerirSequencia(
                      conf.estagios
                        .map((e) => e.tensaoLidaMediaKpa)
                        .filter((m): m is number => m !== null && m !== undefined)
                        .map((m) => String(Math.round(m)))
                        .join(' '))}
                  >
                    Preencher o bloco 3 com os {conf.estagios.length} estágios do arquivo
                  </button>
                </div>
              )}
            </Aviso>
          )}

          <div className="rola">
            <table className="dados">
              <thead>
                <tr>
                  <th>Estágio</th><th>Fase</th>
                  <th className="num">Nominal (kPa)</th>
                  <th className="num">Média lida (kPa)</th>
                  <th className="num">Variação (kPa)</th>
                  <th className="num">Variação (%)</th>
                  <th className="num">Faixa lida (kPa)</th>
                  <th className="num">Leituras</th>
                  <th className="num">Duração</th>
                </tr>
              </thead>
              <tbody>
                {conf.estagios.map((e) => (
                  <tr key={e.etapa} className={e.alerta ? 'atencao' : undefined}>
                    <td className="num">{e.etapa}</td>
                    <td>{e.fase ? rotulo(e.fase) : '— não programado —'}</td>
                    <td className="num">
                      {e.pressaoNominalKpa === null
                        ? '—' : numero(e.pressaoNominalKpa, 2)}
                    </td>
                    <td className="num">
                      {e.tensaoLidaMediaKpa === null
                        ? '—' : numero(e.tensaoLidaMediaKpa, 2)}
                    </td>
                    <td className="num">{sinal(e.desvioKpa, 2)}</td>
                    <td className="num">{sinal(e.desvioPct, 1)}</td>
                    <td className="num">
                      {e.tensaoLidaMinKpa === null ? '—'
                        : `${numero(e.tensaoLidaMinKpa, 1)} a ${numero(e.tensaoLidaMaxKpa, 1)}`}
                    </td>
                    <td className="num">{numero(e.leituras, 0)}</td>
                    <td className="num">{numero(e.tempoFinalMin, 0)} min</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="sub">
            A coluna <b>nominal</b> é a que vai para o laudo e para os
            gráficos. A <b>média lida</b> é ponderada no tempo — cada
            leitura vale o intervalo que ela representa, e não uma "vez",
            porque a aquisição é bem mais densa nos primeiros segundos, quando
            o servo motor ainda está subindo. É dela que sai a variação.
            Estágio destacado passou das duas tolerâncias e precisa ser
            conferido antes da emissão do laudo.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * Nominal × média lida, por estágio.
 *
 * «é bom ter uma conferencia entre o valor da tensao nominal e da tensão
 *  média do estágio e uma coluna com a variação para simples conferência.
 *  Caso tenha uma variação maior que X deverá ser checado antes de
 *  prosseguir com a emissao do relatorio» — Testesolo, 24/08/2026.
 *
 * A tabela aparece SEMPRE que há bruto importado, e não só quando há
 * desvio: quem confere precisa ver que está tudo bem, e não apenas ser
 * avisado quando já está ruim. O estágio fora de tolerância vem
 * destacado e com o botão de conferir; os outros ficam quietos.
 *
 * O que o botão faz NÃO é corrigir a leitura — a leitura do equipamento
 * não se corrige (restrição 25). Ele grava quem olhou, quando e por quê,
 * e com isso a pendência que trava o laudo sai.
 */
function ConferenciaDeTensao({ d, id, recarregar }: {
  d: any; id: string; recarregar: () => void;
}) {
  const { pode } = useSessao();
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const linhas = ((d.tensoes ?? []) as ConferenciaTensao[])
    .filter((t) => t.media_kpa !== null);
  if (!linhas.length) return null;

  const tol = linhas[0];
  const conferir = async (estagioId: string) => {
    if (motivo.trim().length < 3) {
      setErro('Escreva o que foi conferido — aceite sem explicação não é '
        + 'aceite.');
      return;
    }
    setOcupado(true); setErro(null);
    try {
      await conferirTensao(id, estagioId, motivo.trim());
      setAbrindo(null); setMotivo('');
      recarregar();
    } catch (e) {
      setErro(mensagemDoErro(e));
    } finally { setOcupado(false); }
  };

  return (
    <>
      <h3>Conferência da tensão aplicada</h3>
      <div className="rola">
        <table className="dados">
          <thead>
            <tr>
              <th>Estágio</th><th>Fase</th>
              <th className="num">Nominal (kPa)</th>
              <th className="num">Média lida (kPa)</th>
              <th className="num">Variação (kPa)</th>
              <th className="num">Variação (%)</th>
              <th>Situação</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((t) => (
              <tr key={t.estagio_id}
                className={t.fora && !t.conferido ? 'atencao' : undefined}>
                <td className="num">{t.ordem}</td>
                <td>{rotulo(t.fase)}</td>
                <td className="num">{numero(t.nominal_kpa, 2)}</td>
                <td className="num">{numero(t.media_kpa, 2)}</td>
                <td className="num">{sinal(t.desvio_kpa, 2)}</td>
                <td className="num">{sinal(t.desvio_pct, 1)}</td>
                <td>
                  {!t.fora ? <Selo tom="ok">Dentro</Selo>
                    : t.conferido
                      ? (
                        <span title={`${t.justificativa ?? ''} — `
                          + `${t.conferido_por_nome ?? ''}`}>
                          <Selo tom="ok">Conferido</Selo>
                        </span>
                      )
                      : (
                        <>
                          <Selo tom="atencao">Fora — travando o laudo</Selo>
                          {pode('ensaio.processar') && (
                            <button
                              type="button" className="ligacao"
                              onClick={() => {
                                setAbrindo(abrindo === t.estagio_id
                                  ? null : t.estagio_id);
                                setErro(null);
                              }}
                            >
                              conferir
                            </button>
                          )}
                        </>
                      )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {abrindo && (
        <div className="linha-campos">
          <Campo
            rotulo="O que foi conferido"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            dica="Ex.: carga confirmada no manômetro; tara do pendural."
          />
          <button
            type="button" className="principal" disabled={ocupado}
            onClick={() => conferir(abrindo)}
          >
            {ocupado ? 'Gravando…' : 'Registrar conferência'}
          </button>
        </div>
      )}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      <p className="sub">
        A <b>média lida</b> é ponderada no tempo. Um estágio só fica fora
        quando o desvio passa das <b>duas</b> tolerâncias — mais de{' '}
        {numero(Number(tol.tensao_tolerancia_rel) * 100, 0)} % da nominal
        <b>e</b> mais de {numero(tol.tensao_tolerancia_kpa, 0)} kPa. É de
        propósito: o desvio medido nos arquivos da Testesolo é quase
        constante em kPa, então um limite só percentual acusaria todo
        estágio de carga baixa de um ensaio sadio.
      </p>
    </>
  );
}

// ── 6 · as fotos do corpo de prova ───────────────────────────────────

/**
 * As fotos do CP, antes e depois.
 *
 * «deverá ser anexado a foto do CP de antes do ensaio e após o ensaio.
 *  Essas fotos vão no relatório em pdf.»
 *
 * A folha 5 do laudo já existia esperando por elas desde a `0043`. A tela
 * mostra o que falta com o mesmo rigor das pendências: um laudo com folha
 * 5 vazia não avisa que está vazia.
 */
function FotosCp({ d, id, recarregar }: {
  d: any; id: string; recarregar: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const fotos = ((d.fotos ?? []) as any[]).filter((f) => !f.desconsiderada);
  const tem = (t: string) => fotos.some((f) => f.tipo === t);

  const enviar = async (tipo: TipoFoto, lista: File[]) => {
    if (!lista.length) return;
    setOcupado(true); setErro(null);
    try {
      await enviarFotos(id, lista.map((arquivo) => ({ arquivo, tipo })));
      recarregar();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  /*
   * O gabarito é o do MÉTODO, não desta tela.
   *
   * A foto do CP de adensamento é de cima, com o anel inteiro no quadro:
   * é o que faz a foto da moldagem e a do desmonte caírem do mesmo
   * tamanho, lado a lado, na última folha do laudo. Duas fotos tiradas
   * de distâncias diferentes comparam fotografias, não corpos de prova.
   */
  const gabarito = gabaritoDoMetodo('ADNS');

  const par: { tipo: TipoFoto; nome: string; quando: string }[] = [
    { tipo: 'cp_antes', nome: 'CP antes do ensaio', quando: 'na moldagem' },
    { tipo: 'cp_depois', nome: 'CP depois do ensaio', quando: 'no desmonte' },
  ];

  return (
    <section className="bloco">
      <h2>
        5 · Fotos do corpo de prova
        <Estado>
          {fotos.length === 0 ? 'sem foto'
            : `${fotos.length} foto${fotos.length === 1 ? '' : 's'}`}
          {fotos.length > 0 && !tem('cp_antes') && ' · falta a da moldagem'}
          {fotos.length > 0 && !tem('cp_depois') && ' · falta a do desmonte'}
        </Estado>
      </h2>
      <p className="sub">
        As duas primeiras vão para a última folha do laudo. Fotografe na
        moldagem e no desmonte — depois do descarte não há segunda chance.
      </p>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {/*
        `div`, e não `label`, em cada item: um clique em qualquer lugar
        de um `label` dispara o `<input type="file">` que estiver dentro
        dele — e a captura carrega dois, escondidos. A pessoa tocaria no
        nome da foto e a galeria abriria sozinha.
      */}
      <div className="galeria-envio">
        {par.map((p) => (
          <div className="campo" key={p.tipo}>
            <span className="rotulo">
              {p.nome} <span className="sub">({p.quando})</span>{' '}
              {tem(p.tipo)
                ? <Selo tom="ok">Anexada</Selo>
                : <Selo tom="atencao">Falta</Selo>}
            </span>
            <CapturaDeFoto
              tipo={p.tipo}
              titulo={p.nome}
              gabarito={gabarito}
              desabilitado={ocupado}
              aoReceber={(arquivo) => enviar(p.tipo, [arquivo])}
            />
          </div>
        ))}
      </div>

      {fotos.length > 0 && (
        <ul className="lista-fotos">
          {fotos.map((f) => (
            <li key={f.id}>
              <span className="mono">{rotulo(f.tipo)}</span>
              {f.legenda ? ` — ${f.legenda}` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── 1 · cabeçalho ────────────────────────────────────────────────────

export interface BlocoProps {
  d: Ensaio;
  /** O ensaio, para os caminhos literais das chamadas. */
  id: string;
  /** O parque, para o seletor de balança (RN-215, onda 239). */
  equipamentos?: EquipamentoDaLista[];
  gravar(marca: string, chamada: () => Promise<Ensaio>): Promise<boolean>;
  salvando: string | null;
}

/**
 * O que o cadastro da amostra diz sobre o material.
 *
 * Não é campo: é uma FRASE. O tipo e o acondicionamento foram registrados
 * por quem recebeu o material, com a embalagem na frente e fotografia ao
 * lado, e o link leva até lá — corrigir aqui seria criar uma segunda
 * resposta para a mesma pergunta, e a segunda é a que o laudo imprime.
 */
function DoCadastro({ i, amostra }: { i: any; amostra?: Ensaio['amostra'] }) {
  /* O cadastro primeiro; a visão do cabeçalho só quando ele já existe
     (UX-028). Antes desta ordem a tela acusava «a amostra não diz de que
     tipo é» para uma amostra que dizia — a visão é que estava vazia. */
  const tipo = (amostra?.tipo_amostra ?? i?.amostra_tipo) as string | null;
  const acond = (amostra?.tipo_acondicionamento ?? i?.amostra_acondicionamento) as string | null;
  const descricao = (amostra?.acondicionamento_descricao
    ?? i?.amostra_acondicionamento_descricao) as string | null;
  const codigo = (amostra?.codigo ?? i?.amostra_codigo) as string | null;
  /* RN-248 (onda 303): o link segue pelo código; o texto é o nome do cliente. */
  const nome = codigo ? nomeDaAmostra({ codigo,
    identificacao: amostra?.identificacao_recebida ?? i?.amostra_identificacao }) : null;

  if (!tipo) {
    return (
      <Aviso tipo="atencao" titulo="A amostra não diz de que tipo é">
        O laudo imprime «Tipo da amostra» a partir do cadastro, e ele está
        em branco. Corrija na ficha da amostra
        {codigo ? <> (<Link to={`/amostra/${codigo}`}>{nome}</Link>, «Corrigir a ficha»)</> : null}
        {' '}— não há campo aqui para isso, de propósito.
      </Aviso>
    );
  }
  return (
    <p className="sub rodape-regra">
      Tipo da amostra: <strong>{rotulo(tipo)}</strong>
      {acond ? <> — {rotulo(acond)}</> : null}
      {descricao ? <> ({descricao})</> : null}
      {'. '}
      Vem do cadastro de{' '}
      {codigo ? <Link to={`/amostra/${codigo}`}>{nome}</Link> : 'da amostra'},
      e por isso não se digita aqui.
    </p>
  );
}

function Cabecalho({
  d, id, gravar, salvando, campoErro, opcoesAnel, opcoesPrensa, equipamentos = [],
}: BlocoProps & {
  campoErro(c: string): string | undefined;
  opcoesAnel: Opcao[];
  opcoesPrensa: Opcao[];
}) {
  const c = d.cabecalho ?? {};
  const i = d.inicial ?? {};
  const [f, setF] = useState<Numerico>({});
  const [anel, setAnel] = useState<string | null>(null);
  const [tipoAnel, setTipoAnel] = useState<string | null>(null);
  const [condicao, setCondicao] = useState<string | null>(null);
  const [drenagem, setDrenagem] = useState<string | null>(null);
  const [prensa, setPrensa] = useState<string | null>(null);
  /* Qual OBJETO do parque — diferente de `prensa`, que é a marca (0120). */
  const [prensaEquip, setPrensaEquip] = useState<string | null>(null);
  const [origemRhoS, setOrigemRhoS] = useState<string | null>(null);
  const [aberto, setAberto] = useState(!d.cabecalho);
  /* O porquê da troca do anel (UX-038): só aparece quando o anel da tela
     é outro que o gravado e já há leitura. A API recusa a troca muda. */
  const [motivoTroca, setMotivoTroca] = useState('');
  /* A balança das massas do CP (RN-215, onda 239): anel + solo aqui, e a
     massa final no desmonte — a mesma balança, uma escolha. */
  const [balanca, setBalanca] = useState(d.balanca?.id ?? '');
  useEffect(() => { setBalanca(d.balanca?.id ?? ''); }, [d.balanca?.id]);
  const temLeitura = (d.estagios ?? []).some((e) => Number(e.leituras ?? 0) > 0);
  const trocandoAnel = !!c.anel_equipamento_id && !!anel
    && anel !== c.anel_equipamento_id && temLeitura;

  /* O formulário nasce com o que já está gravado — editar é o caso comum. */
  useEffect(() => {
    setAnel(c.anel_equipamento_id ?? null);
    setTipoAnel(c.tipo_anel ?? null);
    setCondicao(c.condicao ?? null);
    setDrenagem(c.drenagem ?? null);
    setPrensa(i.prensa_codigo ?? d.prensas[0]?.codigo ?? null);
    setPrensaEquip(c.prensa_equipamento_id ?? null);
    setOrigemRhoS(i.massa_especifica_graos_origem ?? null);
    /*
     * `paraCampo`, e não o valor cru da API.
     *
     * `numeric(10,3)` chega como `"1.000"`, e o campo mostrava isso a um
     * brasileiro que lê MIL. A pressão de assentamento de 1 kPa aparecia
     * como 1.000 kPa na tela do técnico — Humberto fotografou o defeito
     * sem que nenhum de nós tivesse reparado nele.
     */
    setF({
      massaEspecificaGraos: paraCampo(c.massa_especifica_graos),
      pressaoInundacaoKpa: paraCampo(c.pressao_inundacao_kpa),
      massaAnelSoloG: paraCampo(c.massa_anel_solo_g),
      leituraReferenciaMm: paraCampo(c.leitura_referencia_mm) || '0',
      massaEspecificaGraosFonte: i.massa_especifica_graos_fonte ?? '',
      moldagem: c.moldagem ?? '',
      observacoes: c.observacoes ?? '',
    });
  }, [d.cabecalho, d.inicial]);

  const enviar = () => gravar('cabecalho', () => gravarCabecalho(id, {
    anelEquipamentoId: anel, tipoAnel,
    motivo: trocandoAnel ? motivoTroca.trim() || undefined : undefined,
    moldagem: f.moldagem?.trim() || undefined,
    massaEspecificaGraos: n(f, 'massaEspecificaGraos'),
    massaEspecificaGraosOrigem: origemRhoS ?? undefined,
    massaEspecificaGraosFonte: origemRhoS === 'presumida'
      ? f.massaEspecificaGraosFonte?.trim() || undefined : undefined,
    condicao,
    pressaoInundacaoKpa: n(f, 'pressaoInundacaoKpa'),
    massaAnelSoloG: n(f, 'massaAnelSoloG'),
    leituraReferenciaMm: n(f, 'leituraReferenciaMm') ?? 0,
    prensaCodigo: prensa ?? undefined,
    prensaEquipamentoId: prensaEquip,
    drenagem: drenagem ?? undefined,
    observacoes: f.observacoes?.trim() || undefined,
    balancaEquipamentoId: balanca || undefined,
  }));

  const campo = (chave: string, rot: string, dica?: string) => (
    <Campo
      rotulo={rot} dica={dica} inputMode="decimal" value={f[chave] ?? ''}
      erro={campoErro(chave)}
      onChange={(e) => setF({ ...f, [chave]: e.target.value })}
    />
  );

  const aPrensa = d.prensas.find((p) => p.codigo === prensa);

  return (
    <section className="bloco">
      <h2>
        <button type="button" className="abrir" onClick={() => setAberto(!aberto)}>
          {aberto ? '▾' : '▸'} 1 · Corpo de prova e ensaio
        </button>
        {d.cabecalho && <Selo tom="ok">Lançado</Selo>}
      </h2>

      {!aberto && d.inicial && (
        <dl className="resumo-linha">
          <div><dt>Anel</dt><dd>{i.anel}</dd></div>
          {/* O MESMO e₀ da faixa do topo (UX-045): `indice_vazios` do
              estágio 1 é o índice no FIM do assentamento, não o inicial —
              e a tela mostrava dois números diferentes com o mesmo nome. */}
          <div><dt>e₀</dt><dd>{num(d.estagios[0]?.indice_vazios_inicial, 4)}</dd></div>
          <div><dt>ρd</dt><dd>{num(i.massa_especifica_seca, 4)} g/cm³</dd></div>
          <div><dt>w inicial</dt><dd>{num(i.umidade_inicial, 2)} %</dd></div>
        </dl>
      )}

      {/*
        * POR QUE o índice de vazios não saiu.
        *
        * A `0070` fez a função devolver nulo em vez de levantar exceção —
        * exceção de dentro de uma view derruba a ficha onde o número seria
        * corrigido. A `0075` trouxe a explicação até aqui.
        *
        * Sem ela, o `e₀` some e a tela deixa o técnico entre duas
        * hipóteses opostas: «não foi medido» e «está impossível». É o
        * CA-29 — toda célula com estado explícito. A frase vem do banco,
        * de uma função só, e não é montada aqui.
        */}
      {i?.problema_indice_vazios && (
        <Aviso tipo="atencao" titulo="Índice de vazios não calculado">
          {i.problema_indice_vazios}
        </Aviso>
      )}

      <DoCadastro i={i} amostra={d.amostra} />

      {aberto && (
        <div className="form">
          {/*
            * Digitando o número do anel.
            *
            * Com seis anéis de adensamento os botões bastavam; com os 32
            * de cisalhamento e os 50 discos na mesma tela não bastam, e
            * duas maneiras de escolher a mesma coisa em telas vizinhas é
            * o que faz o técnico hesitar. Uma só, e é a que ele pediu
            * (Testesolo, 01/09/2026).
            */}
          <EscolhaPorCodigo
            rotulo="Anel de adensamento"
            opcoes={opcoesAnel.map(comoCodigo)}
            valor={anel ?? ''}
            aoEscolher={setAnel}
            erro={campoErro('anelEquipamentoId')}
            dica="Digite o número do anel. As dimensões vêm da calibração interna dele — não se digitam aqui."
          />
          {trocandoAnel && (
            <Area
              rotulo="Por que o anel está sendo trocado"
              rows={2}
              value={motivoTroca}
              onChange={(e) => setMotivoTroca(e.target.value)}
              erro={campoErro('motivo')}
              dica="Já há leituras lançadas: trocar o anel muda a altura de sólidos, o índice de vazios e o cv de todos os estágios. O motivo vai para a trilha, ao lado do anel antigo e do novo (UX-038)."
            />
          )}
          {/*
            * A prensa, pelo código patrimonial (`0120`).
            *
            * «Owntec MS 115» é a marca, e a marca não identifica objeto:
            * duas MS 115 na bancada são duas prensas, com dois números de
            * série e duas calibrações. O laudo precisa dizer QUAL delas
            * mediu — e a escolha aqui é a mesma do anel, para o técnico
            * não aprender dois jeitos de escolher equipamento.
            */}
          <EscolhaPorCodigo
            rotulo="Prensa de adensamento"
            opcoes={opcoesPrensa.map(comoCodigo)}
            valor={prensaEquip ?? ''}
            aoEscolher={setPrensaEquip}
            erro={campoErro('prensaEquipamentoId')}
            dica="Digite o código da prensa. É ela que sai no laudo, com a
              calibração vigente no dia do ensaio."
          />
          <Escolha
            rotulo="Tipo de anel" valor={tipoAnel} aoEscolher={setTipoAnel}
            limpavel={false} erro={campoErro('tipoAnel')}
            opcoes={[{ valor: 'fixo', texto: 'Fixo' },
                     { valor: 'flutuante', texto: 'Flutuante' }]}
          />
          <Escolha
            rotulo="Condição de ensaio" valor={condicao} aoEscolher={setCondicao}
            limpavel={false} erro={campoErro('condicao')}
            opcoes={[{ valor: 'inundado', texto: 'Inundado' },
                     { valor: 'sem_inundacao', texto: 'Sem inundação' }]}
          />
          <Escolha
            rotulo="Drenagem do CP" valor={drenagem} aoEscolher={setDrenagem}
            erro={campoErro('drenagem')}
            opcoes={[{ valor: 'dupla', texto: 'Dupla' },
                     { valor: 'simples', texto: 'Simples' }]}
            dica="Entra no cálculo da altura de drenagem — e no laudo."
          />

          {campo('pressaoInundacaoKpa', 'Pressão de assentamento (kPa)',
            'Solos muito moles: definir · moles: 2 · resistentes: 5')}
          {campo('massaAnelSoloG', 'Massa anel + solo, inicial (g)')}
          <EscolhaDeBalanca rotulo="Balança das massas do corpo de prova"
            equipamentos={equipamentos} valor={balanca} aoEscolher={setBalanca}
            erro={campoErro('balancaEquipamentoId')} />

          {/*
            * ρs e a origem dele, juntos.
            *
            * Separá-los abriria a janela em que o número já está gravado e a
            * origem não — e é nessa janela que um laudo sai afirmando o que
            * ninguém declarou.
            */}
          {campo('massaEspecificaGraos', 'ρs — massa esp. dos sólidos (g/cm³)')}
          <Escolha
            rotulo="Origem do ρs" valor={origemRhoS} aoEscolher={setOrigemRhoS}
            erro={campoErro('massaEspecificaGraosOrigem')}
            opcoes={[{ valor: 'medida', texto: 'Medida em ensaio' },
                     { valor: 'presumida', texto: 'Valor presumido' }]}
            dica="Valor presumido é premissa, e o laudo declara premissa."
          />
          {origemRhoS === 'presumida' && (
            <Campo
              rotulo="De onde vem o valor adotado"
              value={f.massaEspecificaGraosFonte ?? ''}
              erro={campoErro('massaEspecificaGraosFonte')}
              onChange={(e) =>
                setF({ ...f, massaEspecificaGraosFonte: e.target.value })}
              dica="Literatura, material semelhante, dado do cliente."
            />
          )}
          {origemRhoS === 'medida' && (
            <Aviso tipo="info">
              O vínculo com o ensaio de massa específica dos sólidos é gravado
              quando esse ensaio existir e estiver aprovado, na mesma amostra.
              Enquanto isso o campo fica sem origem e o laudo avisa.
            </Aviso>
          )}

          {campo('leituraReferenciaMm', 'Leitura de referência (mm)',
            'A leitura do deflectômetro 5 min após a pressão de assentamento.')}

          {/*
            * A prensa, no lugar da antiga pergunta «sentido do relógio».
            *
            * O sentido é do equipamento, não do ensaio: o arquivo da
            * Owntec traz o deslocamento crescendo enquanto o corpo de
            * prova encurta. Escolher a prensa responde a pergunta uma vez,
            * no catálogo, em vez de responder a cada ensaio.
            */}
          <Escolha
            rotulo="Prensa que registra as leituras" valor={prensa}
            aoEscolher={setPrensa} limpavel={false}
            opcoes={d.prensas.map((p) => ({ valor: p.codigo, texto: p.nome }))}
            dica="O sentido da leitura vem daqui — não se pergunta mais a cada ensaio."
          />
          {aPrensa && (
            <p className="sub">
              Convenção do arquivo:{' '}
              <code>{aPrensa.coluna_deslocamento ?? 'deslocamento'}</code>{' '}
              {aPrensa.leitura_sentido === 1
                ? 'cresce' : 'diminui'} enquanto o corpo de prova encurta.
            </p>
          )}

          <Area rotulo="Moldagem" value={f.moldagem ?? ''} rows={2}
            onChange={(e) => setF({ ...f, moldagem: e.target.value })} />
          <Area rotulo="Observações do ensaio" value={f.observacoes ?? ''} rows={2}
            onChange={(e) => setF({ ...f, observacoes: e.target.value })} />

          <button type="button" className="principal"
            disabled={salvando === 'cabecalho'} onClick={() => void enviar()}>
            {salvando === 'cabecalho' ? 'Gravando…' : 'Gravar cabeçalho'}
          </button>
        </div>
      )}
    </section>
  );
}

// ── 2 e 5 · a planilha de umidade ────────────────────────────────────

/**
 * A planilha de umidade de um momento — estática.
 *
 * A versão anterior tinha um formulário «acrescentar» embaixo da tabela:
 * escolher momento, escolher cápsula, digitar duas massas, clicar, e a
 * linha aparecia. Isso inverte a ordem da bancada. As três cápsulas são
 * pesadas em sequência, na mesma balança, e o que existe na mão do
 * técnico é uma PLANILHA DE TRÊS LINHAS — não um botão.
 *
 * Aqui as três linhas nascem abertas. A tara não é digitada: vem da
 * aferição vigente da cápsula escolhida, e aparece na coluna assim que a
 * planilha é gravada — que é a prova, na tela, de que o número usado é o
 * do certificado e não o da memória de alguém.
 *
 * Linha descartada continua ocupando o seu número e ganha uma linha
 * extra ao final para a substituta: `LINHAS_UMIDADE` é piso, não teto.
 */
function Umidades({
  titulo, momento, explicacao, d, id, gravar, salvando, recarregar,
  equipamentos = [],
}: BlocoProps & {
  titulo: string; momento: 'inicial' | 'final'; explicacao: string;
  recarregar(): void;
}) {
  const listaId = `capsulas-${momento}`;
  /* As cápsulas na estufa e o carimbo das pesagens (onda 294, RN-241). */
  const estufa = useEstufaDoEnsaio(id, d.umidades);
  const congelado = ['aprovado', 'cancelado', 'substituido'].includes(d.status ?? '');
  const opcoesCapsula: Opcao[] = d.capsulas.map((c) => ({
    valor: c.id, texto: c.codigo_patrimonial }));
  /* A balança das cápsulas (RN-215, onda 239) — a mesma no início e no
     desmonte: uma escolha para a bateria. */
  const [balancaUmidade, setBalancaUmidade] = useState(d.balancaUmidade?.id ?? '');
  useEffect(() => { setBalancaUmidade(d.balancaUmidade?.id ?? ''); },
    [d.balancaUmidade?.id]);
  const [descartando, setDescartando] = useState<Umidade | null>(null);

  /*
   * O código da cápsula é DIGITADO, não escolhido numa fileira de botões.
   *
   * Humberto: «o usuário entra o número da cápsula». Numa bancada com
   * quarenta cápsulas, uma fileira de botões é uma fileira de quarenta —
   * e o técnico já tem o número gravado na tampa, na mão. O `<datalist>`
   * completa enquanto ele digita e não atrapalha quem digita direto.
   *
   * O que se guarda continua sendo o `id` do equipamento: o código é a
   * forma de ACHAR a cápsula, não o que amarra o ensaio a ela. Cápsula
   * recodificada não pode reescrever ensaio antigo.
   */
  const porCodigo = useMemo(() => {
    const m = new Map<string, Capsula>();
    for (const c of d.capsulas) m.set(c.codigo_patrimonial.toUpperCase(), c);
    return m;
  }, [d.capsulas]);
  const porId = useMemo(() => {
    const m = new Map<string, Capsula>();
    for (const c of d.capsulas) m.set(c.id, c);
    return m;
  }, [d.capsulas]);
  const gravadas = useMemo(
    () => d.umidades.filter((u) => u.momento === momento)
      .sort((a, b) => a.ordem - b.ordem),
    [d.umidades, momento]);

  /*
   * Quantas linhas mostrar: as três de sempre, mais uma para cada
   * descartada — a substituta precisa de lugar —, e nunca menos do que o
   * que já está gravado.
   */
  const quantas = Math.max(
    LINHAS_UMIDADE + gravadas.filter((u) => u.descartada).length,
    gravadas.length,
    LINHAS_UMIDADE);
  const ordens = Array.from({ length: quantas }, (_, k) => k + 1);

  const [f, setF] = useState<Record<number, Numerico>>({});
  /** O que está DIGITADO na coluna da cápsula, linha a linha. */
  const [codigos, setCodigos] = useState<Record<number, string>>({});

  /*
   * A linha gravada não volta para a planilha (onda 294, RN-236): ela
   * trava, e a correção é pelo «Corrigir» da coluna seca. A planilha
   * recomeça vazia nas linhas que ainda não foram gravadas.
   */
  useEffect(() => { setF({}); setCodigos({}); }, [d.umidades, momento]);

  const linha = (ordem: number) => f[ordem] ?? {};
  /** A cápsula que o texto da linha nomeia — `undefined` se não nomeia nenhuma. */
  const capsulaDe = (ordem: number) =>
    porCodigo.get((codigos[ordem] ?? '').trim().toUpperCase());

  /*
   * Sobe só a linha NOVA com cápsula e massa úmida. A seca é opcional:
   * sem ela, a cápsula vai para a estufa e se pesa depois (RN-241).
   */
  const prontas = ordens
    .filter((ordem) => !gravadas.some((g) => g.ordem === ordem))
    .map((ordem) => ({
      ordem,
      capsulaEquipamentoId: capsulaDe(ordem)?.id ?? null,
      massaCapsulaSoloUmido: n(linha(ordem), 'umido'),
      ...(n(linha(ordem), 'seco') !== undefined
        ? { massaCapsulaSoloSeco: n(linha(ordem), 'seco') } : {}),
    }))
    .filter((l) => l.capsulaEquipamentoId
      && l.massaCapsulaSoloUmido !== undefined);
  const naEstufa = gravadas.filter(naEstufaDaFicha).length;
  const novasNaPlanilha = ordens.filter((o) => !gravadas.some((g) => g.ordem === o)).length;

  const enviar = () => gravar(`umidade-${momento}`,
    () => gravarUmidades(id, {
      momento, determinacoes: prontas,
      ...(balancaUmidade ? { balancaUmidadeEquipamentoId: balancaUmidade } : {}),
    }));

  /* Descartar abre a janela com o motivo — o `window.prompt` de antes era
     um diálogo do navegador, que trava a página (onda 239). */
  const descartar = async (u: Umidade) => { setDescartando(u); };

  const media = momento === 'inicial'
    ? d.inicial?.umidade_inicial : d.inicial?.umidade_final;

  const descartadas = gravadas.filter((u) => u.descartada).length;
  const valem = gravadas.length - descartadas;

  return (
    <section className="bloco">
      <h2>
        {titulo}
        <Estado>
          {valem === 0 ? 'nenhuma determinação'
            : `média ${num(media, 2)} % · ${valem} determinaç${valem === 1 ? 'ão' : 'ões'}`}
          {descartadas > 0 && ` · ${descartadas} descartada${descartadas === 1 ? '' : 's'}`}
        </Estado>
      </h2>
      <p className="sub">{explicacao}</p>

      <div className="fe-campos">
        <EscolhaDeBalanca rotulo="Balança das cápsulas"
          equipamentos={equipamentos} valor={balancaUmidade}
          aoEscolher={setBalancaUmidade} />
      </div>
      <JanelaDeDescarte
        alvo={descartando ? `a determinação ${descartando.ordem}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={async (motivo) => {
          if (!descartando) return false;
          const feito = await gravar('descarte',
            () => descartarUmidade(id, descartando.id, motivo));
          if (feito) recarregar();
          return feito;
        }}
        salvando={salvando !== null}
      />

      <datalist id={listaId}>
        {d.capsulas.map((c) => (
          <option key={c.id} value={c.codigo_patrimonial}>
            {c.tara != null ? `tara ${num(c.tara, 4)} g` : 'sem calibração interna'}
          </option>
        ))}
      </datalist>

      <div className="rola">
        <table className="dados planilha" {...tecladoDaTabela(`Umidade ${momento}`)}>
          <thead>
            <tr>
              <th>Nº</th>
              <th>Cápsula</th>
              <th className="num">Tara (g)</th>
              <th className="num">Cáps. + solo úmido (g)</th>
              <th className="num">Cáps. + solo seco (g)</th>
              <th className="num">w (%)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ordens.map((ordem) => {
              const u = gravadas.find((g) => g.ordem === ordem);
              /* Gravada é travada (RN-236); descartada, além disso, riscada. */
              const travada = !!u;
              return (
                <tr key={ordem} className={u?.descartada ? 'descartada' : undefined}>
                  <td>{ordem}</td>
                  <td>
                    {travada ? <span className="cod">{u!.capsula
                      ?? porId.get(u!.capsula_equipamento_id ?? '')?.codigo_patrimonial}</span> : (
                      <Campo
                        rotulo="" list={listaId} autoComplete="off"
                        placeholder="código"
                        value={codigos[ordem] ?? ''}
                        erro={(codigos[ordem] ?? '').trim() && !capsulaDe(ordem)
                          ? 'não existe cápsula com este código' : undefined}
                        onChange={(e) =>
                          setCodigos({ ...codigos, [ordem]: e.target.value })}
                      />
                    )}
                  </td>
                  {/*
                    * A tara NÃO é campo. Ela aparece assim que o código é
                    * reconhecido, lida da aferição vigente na data do
                    * ensaio, e é essa conferência — «peguei a cápsula
                    * certa?» — que ela serve. Depois de gravado vale o
                    * número que o servidor devolveu, que é o mesmo.
                    */}
                  <td className="num mono">
                    {u?.tara != null ? num(u.tara, 4)
                      : capsulaDe(ordem)?.tara != null
                        ? num(capsulaDe(ordem)!.tara, 4)
                        : <span className="nota">da calibração interna</span>}
                  </td>
                  <td className="num">
                    {travada ? num(u!.massa_capsula_solo_umido, 4) : (
                      <Campo
                        rotulo="" inputMode="decimal"
                        value={linha(ordem).umido ?? ''}
                        onChange={(e) => setF({
                          ...f, [ordem]: { ...linha(ordem), umido: e.target.value } })}
                      />
                    )}
                  </td>
                  <td className="num">
                    {travada ? (
                      <SecaDaLinha linha={u} estufa={estufa} congelado={congelado}
                        opcoesCapsula={opcoesCapsula} casas={4} aoMudou={recarregar} />
                    ) : (
                      <Campo
                        rotulo="" inputMode="decimal" placeholder="ou na estufa"
                        value={linha(ordem).seco ?? ''}
                        onChange={(e) => setF({
                          ...f, [ordem]: { ...linha(ordem), seco: e.target.value } })}
                      />
                    )}
                  </td>
                  <td className="num mono">{u ? <WDaLinha linha={u} /> : '—'}</td>
                  <td>
                    {u && (u.descartada
                      ? <span className="nota" title={u.motivo_descarte ?? ''}>
                          descartada
                        </span>
                      : (
                        <button type="button" className="discreto"
                          onClick={() => void descartar(u)}>
                          descartar
                        </button>
                      ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={5}>Média {momento}</th>
              <td className="num mono">{num(media, 2)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {!congelado && <LembreteDaEstufa quantas={naEstufa} />}
      <div className="acoes">
        <button type="button" className="principal" data-gravar-tabela=""
          disabled={!prontas.length || salvando === `umidade-${momento}`}
          onClick={() => void enviar()}>
          {salvando === `umidade-${momento}`
            ? 'Gravando…'
            : `Gravar planilha (${prontas.length} de ${novasNaPlanilha})`}
        </button>
      </div>
      <p className="sub">
        Linha em branco não sobe, e linha gravada trava: a pesagem errada se
        corrige pelo «Corrigir» (dentro da janela, ou com a permissão e o
        motivo) ou se descarta com motivo.
      </p>
    </section>
  );
}

// ── 3 · estágios ─────────────────────────────────────────────────────

/**
 * A sequência de pressões, digitada de uma vez.
 *
 * O campo aceita a sequência inteira separada por espaço, vírgula ou
 * quebra de linha — `2,5 5 10 20 40 80 160 320 640 320 80 20` —, que é
 * como ela está escrita na ficha da bancada. Digitar doze linhas de
 * formulário para transcrever uma linha de ficha é o tipo de atrito que
 * faz a planilha ganhar.
 *
 * A vírgula é ambígua em pt-BR (separador decimal E de lista). A regra:
 * vírgula seguida de dígito é decimal; qualquer outra coisa separa.
 */
function Estagios({ d, id, gravar, salvando, sugestao }: BlocoProps & {
  sugestao?: string | null;
}) {
  const [texto, setTexto] = useState('');
  const [assentamento, setAssentamento] = useState(true);
  const [veioDoArquivo, setVeioDoArquivo] = useState(false);

  /* A sugestão do bloco 6 entra no campo; a pessoa ajusta e grava. */
  useEffect(() => {
    if (sugestao) {
      setTexto(sugestao);
      setAssentamento(false);
      setVeioDoArquivo(true);
    }
  }, [sugestao]);

  const lidos = useMemo(() => {
    const partes = texto
      .replace(/,(?=\D|$)/g, ' ')
      .split(/[\s;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    const vals = partes.map((p) => paraNumero(p));
    return vals.every((v) => v !== undefined) ? (vals as number[]) : null;
  }, [texto]);

  /* A mesma regra do servidor, só para pré-visualizar (`RN-152`). */
  const comFase = useMemo(() => {
    if (!lidos) return [];
    let ant: number | null = null;
    return lidos.map((p, k) => {
      const fase = k === 0 && assentamento ? 'assentamento'
        : ant !== null && p < ant ? 'descarregamento' : 'carregamento';
      ant = p;
      return { ordem: k + 1, pressaoKpa: p, fase };
    });
  }, [lidos, assentamento]);

  const enviar = () => gravar('estagios', () => gravarEstagios(id, {
    estagios: comFase.map((e) => ({
      ordem: e.ordem, pressaoKpa: e.pressaoKpa,
      assentamento: e.fase === 'assentamento',
    })),
  }));

  return (
    <section className="bloco" id="bloco-estagios">
      <h2>
        3 · Estágios de pressão
        <Estado>
          {d.estagios.length === 0 ? 'nenhum lançado'
            : `${d.estagios.length} estágios · último `
              + `${num(d.estagios[d.estagios.length - 1].pressao_kpa, 1)} kPa`}
        </Estado>
      </h2>

      {d.estagios.length > 0 && (
        <div className="rola">
          <table className="dados">
            <thead>
              <tr>
                <th>#</th><th className="num">σ (kPa)</th><th>Fase</th>
                <th className="num">Altura (mm)</th>
                <th className="num">e</th><th className="num">Leituras</th>
              </tr>
            </thead>
            <tbody>
              {d.estagios.map((s) => (
                <tr key={s.id}>
                  <td>{s.ordem}</td>
                  <td className="num">{num(s.pressao_kpa, 1)}</td>
                  <td>
                    <Selo tom={s.fase === 'descarregamento' ? 'atencao' : 'neutro'}>
                      {rotulo(s.fase)}
                    </Selo>
                  </td>
                  <td className="num">
                    {s.altura_cm != null ? num(Number(s.altura_cm) * 10, 4) : '—'}
                  </td>
                  <td className="num">{num(s.indice_vazios, 4)}</td>
                  <td className="num">{s.leituras}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="form">
        <Area
          rotulo="Sequência de pressões (kPa)"
          rows={2} value={texto}
          onChange={(e) => setTexto(e.target.value)}
          dica="Separadas por espaço ou quebra de linha. Ex.: 2,5 5 10 20 40 80 160 320 640 320 80 20"
        />
        <label className="caixa">
          <input type="checkbox" checked={assentamento}
            onChange={(e) => setAssentamento(e.target.checked)} />
          O primeiro é o estágio de assentamento
        </label>

        {veioDoArquivo && texto.trim() && (
          <Aviso tipo="atencao" titulo="Sequência sugerida pelo arquivo da prensa">
            Estes são os valores <b>médios lidos</b> em cada estágio,
            arredondados — não a tensão nominal. Ajuste cada um para a tensão
            nominal que foi programada (a que vai para o laudo) e grave. Se o
            primeiro for assentamento, marque abaixo.
          </Aviso>
        )}
        {texto.trim() && !lidos && (
          <Aviso tipo="erro">Há um valor que não é número na sequência.</Aviso>
        )}

        {comFase.length > 0 && (
          <p className="sub">
            {comFase.length} estágios ·{' '}
            {comFase.filter((e) => e.fase === 'carregamento').length} de
            carregamento ·{' '}
            {comFase.filter((e) => e.fase === 'descarregamento').length} de
            descarregamento. A fase sai da sequência, não do que se digita.
          </p>
        )}

        <button type="button" className="principal"
          disabled={!comFase.length || salvando === 'estagios'}
          onClick={() => void enviar()}>
          {salvando === 'estagios' ? 'Gravando…' : 'Gravar sequência'}
        </button>
      </div>
    </section>
  );
}

// ── 4 · leituras (conferência) ───────────────────────────────────────

/**
 * As leituras que vieram da prensa — para CONFERIR e descartar, não para
 * digitar.
 *
 * ─── Por que o campo de colar sumiu ─────────────────────────────────────
 *
 * «não haverão leituras avulsas do deflectômetro. Portanto elimine essa
 *  seção.» — Humberto, 29/08/2026.
 *
 * Havia aqui uma área de texto onde o laboratorista colava
 * `tempo<tab>leitura` do equipamento. Ela fazia sentido enquanto o
 * caminho era a planilha; deixou de fazer quando o arquivo da Owntec
 * passou a entrar inteiro pelo bloco acima. Duas portas para o mesmo
 * dado é uma a mais — e a que se digita é a que erra, porque um número
 * colado fora de ordem entra sem que nada acuse.
 *
 * ─── Por que a LISTA ficou ──────────────────────────────────────────────
 *
 * Porque descartar não é digitar. A prensa registra ponto durante a
 * troca de carga e durante queda de energia, e esses pontos deformam a
 * curva; tirá-los do traçado é trabalho de bancada legítimo. O que some
 * é a entrada de dado que não existe — não a conferência do dado que
 * existe.
 *
 * A lista continua aqui, embaixo do arquivo de onde as leituras vieram.
 */
function Leituras({ d, id, gravar, salvando }: BlocoProps) {
  const [estagio, setEstagio] = useState<string | null>(null);
  const [lista, setLista] = useState<Leitura[] | null>(null);

  useEffect(() => {
    if (!estagio) { setLista(null); return; }
    void (async () => {
      try {
        setLista(await verLeituras(id, estagio));
      } catch { setLista([]); }
    })();
  }, [estagio, id, d]);

  /**
   * Descartar uma leitura — que não é apagar.
   *
   * A linha fica, riscada, com o motivo ao lado. O dado bruto é
   * evidência (restrição 25 do briefing): sai do traçado, não da base.
   */
  const [descartando, setDescartando] = useState<Leitura | null>(null);
  /* A janela com o motivo, e não o `window.prompt` (onda 239). */
  const descartar = async (l: Leitura) => { setDescartando(l); };
  const confirmarDescarte = async (motivo: string) => {
    if (!descartando) return false;
    const feito = await gravar('descarte-leitura',
      () => descartarLeitura(id, descartando.id, motivo));
    if (feito) { setLista(null); setEstagio(estagio); }
    return feito;
  };

  const opcoes: Opcao[] = d.estagios.map((s) => ({
    valor: s.id,
    texto: `${s.ordem} · ${num(s.pressao_kpa, 1)} kPa`
      + (s.leituras ? ` (${s.leituras})` : ''),
  }));

  const total = d.estagios.reduce((soma, s) => soma + (s.leituras ?? 0), 0);

  return (
    <section className="bloco">
      <JanelaDeDescarte
        alvo={descartando ? 'a leitura do traçado' : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={confirmarDescarte}
        salvando={salvando !== null}
      />
      <h2>
        7 · Leituras da prensa
        <Estado>
          {total === 0 ? 'nenhuma leitura'
            : `${total} leituras · `
              + `${d.estagios.filter((s) => s.leituras > 0).length} de `
              + `${d.estagios.length} estágios`}
        </Estado>
      </h2>
      <p className="sub">
        Elas entram pelo arquivo do equipamento, acima. Aqui se confere e,
        quando a prensa registrou ponto durante a troca de carga, se
        descarta do traçado — com o motivo, e sem sair do banco.
      </p>

      {total === 0 ? (
        <p className="nada">
          Nenhuma leitura ainda. Importe o arquivo da prensa no bloco 6.
        </p>
      ) : (
        <>
          <Escolha rotulo="Estágio" opcoes={opcoes} valor={estagio}
            aoEscolher={setEstagio} classe="rolar" />

          {estagio && lista && lista.length > 0 && (
            <details className="lista-leituras">
              <summary>{lista.length} leituras gravadas</summary>
              <div className="rola">
                <table className="dados compacta">
                  <thead>
                    <tr><th className="num">t (s)</th><th className="num">mm</th>
                      <th>Origem</th><th /></tr>
                  </thead>
                  <tbody>
                    {lista.map((l) => (
                      <tr key={l.id}
                        className={l.descartada ? 'descartada' : undefined}>
                        <td className="num">{num(l.tempo_s, 0)}</td>
                        <td className="num">{num(l.leitura_mm, 4)}</td>
                        <td>{rotulo(l.origem)}</td>
                        <td>
                          {l.descartada
                            ? (
                              <span className="nota" title={l.motivo_descarte ?? ''}>
                                descartada
                              </span>
                            )
                            : (
                              <button type="button" className="discreto"
                                disabled={salvando === 'descarte-leitura'}
                                onClick={() => void descartar(l)}>
                                descartar
                              </button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {estagio && lista && lista.length === 0 && (
            <p className="nada">Este estágio não recebeu leitura nenhuma.</p>
          )}
        </>
      )}
    </section>
  );
}

// ── 5 · fechamento ───────────────────────────────────────────────────

/**
 * O desmonte: massa final e umidade final.
 *
 * As duas coisas acontecem no mesmo ato, no mesmo dia — o corpo de prova
 * sai do anel, vai à balança, vai à estufa. Estavam separadas: a massa
 * final no formulário de abertura, a umidade final num seletor de
 * momento no meio da planilha inicial. Nenhuma das duas ficava onde o
 * técnico as produz.
 */
function Fechamento({
  d, id, gravar, salvando, campoErro, recarregar, equipamentos = [],
}: BlocoProps & {
  campoErro(c: string): string | undefined;
  recarregar(): void;
}) {
  const i = d.inicial ?? {};
  const [massa, setMassa] = useState('');
  const [balanca, setBalanca] = useState(d.balanca?.id ?? '');
  useEffect(() => { setBalanca(d.balanca?.id ?? ''); }, [d.balanca?.id]);

  useEffect(() => { setMassa(paraCampo(i.massa_final_g)); }, [d.inicial]);

  const enviar = () => gravar('fechamento', () => gravarFechamento(id, {
    massaFinalG: paraNumero(massa),
    ...(balanca ? { balancaEquipamentoId: balanca } : {}),
  }));

  return (
    <>
      <section className="bloco">
        <h2>
          4 · Fechamento do ensaio — desmonte
          {i.massa_final_g != null && i.umidade_final != null
            && <Selo tom="ok">Completo</Selo>}
        </h2>
        <p className="sub">
          O que se mede no desmonte, quando o corpo de prova sai do anel
          (6.5.7). Antes do desmonte estes campos não têm resposta — e por
          isso não aparecem mais no formulário de abertura.
        </p>

        <div className="form linha">
          <Campo
            rotulo="Massa final (g)" inputMode="decimal" value={massa}
            erro={campoErro('massaFinalG')}
            onChange={(e) => setMassa(e.target.value)}
            dica="Conjunto pesado no desmonte, na mesma balança da moldagem."
          />
          <EscolhaDeBalanca rotulo="Balança (a mesma da moldagem)"
            equipamentos={equipamentos} valor={balanca} aoEscolher={setBalanca}
            erro={campoErro('balancaEquipamentoId')} />
          <button type="button" className="principal"
            disabled={paraNumero(massa) === undefined || salvando === 'fechamento'}
            onClick={() => void enviar()}>
            {salvando === 'fechamento' ? 'Gravando…' : 'Gravar massa final'}
          </button>
        </div>
      </section>

      <Umidades
        equipamentos={equipamentos}
        titulo="4b · Umidade final" momento="final"
        explicacao="Três determinações do material do desmonte (6.5.7). É o
          par da umidade inicial no quadro Inicial × Final do laudo."
        d={d} id={id} gravar={gravar} salvando={salvando}
        recarregar={recarregar}
      />
    </>
  );
}

// ── 6 · o que a outra mesa já traçou ─────────────────────────────────

/**
 * A interpretação, vista daqui — e só vista.
 *
 * O laboratorista precisa saber se cv, Cc e σ'p já foram traçados: é o
 * que diz a ele se o ensaio dele acabou de verdade. Precisa não poder
 * traçá-los: a segregação de funções do briefing (§15) existe para que
 * quem executa não seja também quem interpreta e aprova o próprio
 * trabalho.
 *
 * Por isso aqui há contagem e link, e nenhum campo.
 */
function Interpretacao({ d, id, podeProcessar }: {
  d: Ensaio; id: string; podeProcessar: boolean;
}) {
  const comCv = d.estagios.filter((s) => s.cv_metodo).length;
  const carga = d.estagios.filter((s) => s.fase === 'carregamento').length;

  return (
    <section className="bloco">
      <h2>8 · Interpretação</h2>
      <p className="sub">
        cv, Cr/Cc/Cs e σ'p são construções gráficas traçadas sobre a curva,
        e assinadas por quem as traçou. Não se lançam na bancada.
      </p>

      <dl className="resumo-linha">
        <div><dt>cv traçado</dt><dd>{comCv} de {carga} estágios</dd></div>
        <div><dt>Índices</dt><dd>{d.indices.length} de 3 trechos</dd></div>
        <div>
          <dt>σ'p</dt>
          <dd>{d.pre ? `${num(d.pre.pressao_kpa, 1)} kPa` : 'não traçada'}</dd>
        </div>
      </dl>

      <div className="acoes">
        <Link className="botao" to={`/ensaio/${id}/adensamento/interpretacao`}>
          {podeProcessar ? 'Abrir a interpretação' : 'Ver a interpretação'}
        </Link>
      </div>
      {!podeProcessar && (
        <p className="sub rodape-regra">
          Traçar exige a permissão <code>ensaio.interpretar</code>. Você pode
          abrir e conferir; os campos ficam fechados.
        </p>
      )}
    </section>
  );
}

// ── 7 · laudo ────────────────────────────────────────────────────────

/**
 * O laudo, e o aviso de que ele é preliminar.
 *
 * O documento sai marcado PRELIMINAR enquanto não houver relatório
 * aprovado. Não é rascunho: é um PDF completo, com os números que o banco
 * calculou, que serve para conferir — e cuja marca d'água existe para que
 * ninguém o mande ao cliente por engano.
 */
/**
 * O bloco do laudo — o mesmo em duas telas, com dois números.
 *
 * Ele é o último da bancada, onde a sequência vai até nove, e é também o
 * último da interpretação, onde ela vai até três. Com o número cravado,
 * a interpretação mostrava «1 · 2 · 3 · 9», e o salto fazia quem confere
 * procurar quatro blocos que não existem naquela tela.
 *
 * É o mesmo defeito de 01/09/2026 na bancada, por outro caminho: lá dois
 * blocos se chamavam «5»; aqui um bloco leva o número da tela vizinha. O
 * número é a ORDEM DESTA tela — quem sabe qual é ela é quem a monta.
 */
export function Laudo({ id, pendencias, numero: numeroDoBloco = 9 }: {
  id: string; pendencias: string[]; numero?: number;
}) {
  return (
    <section className="bloco laudo">
      <h2>{numeroDoBloco} · Laudo</h2>
      {pendencias.length > 0 && (
        <Aviso tipo="atencao">
          O laudo sai assim mesmo, com os campos que faltam marcados no
          documento. Emitir com pendência é decisão de quem verifica — o
          sistema mostra o buraco em vez de escondê-lo.
        </Aviso>
      )}
      <AcoesDoLaudo ensaioId={id} />
      <p className="sub">
        O PDF sai com marca d'água PRELIMINAR até haver relatório aprovado
        que o ampare (<code>PD-85</code>).
      </p>
    </section>
  );
}
